import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Check, Loader2, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

const CHECKS = [
  ["session_as_planned", "Session running as planned"],
  ["uniform_ok", "Staff in uniform"],
  ["has_tablet", "Staff has their tablet"],
  ["has_id_card", "Staff has their ID card"],
];
const RATINGS = [
  ["rating_professionalism", "Professionalism"],
  ["rating_safety", "Safety"],
  ["rating_engagement", "Child engagement"],
];
const EMPTY = {
  session_as_planned: "", uniform_ok: "", has_tablet: "", has_id_card: "",
  rating_professionalism: 0, rating_safety: 0, rating_engagement: 0, notes: "",
};

const STATUS_BADGES = {
  scheduled: "bg-amber-50 text-amber-700 border-transparent",
  completed: "bg-emerald-50 text-emerald-700 border-transparent",
  cancelled: "bg-slate-100 text-slate-500 border-transparent",
};

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2">
      {["yes", "no"].map((v) => (
        <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors duration-150 ${
            value === v ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-border hover:border-primary/50"
          }`}>
          {v === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

export default function SpotChecksPanel({ staffId, childId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (staffId) params.staff_id = staffId;
      if (childId) params.child_id = childId;
      const { data } = await api.get("/spot-checks", { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [staffId, childId]);

  useEffect(() => { load(); }, [load]);

  const valid = CHECKS.every(([k]) => form[k]) && RATINGS.every(([k]) => form[k] >= 1);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/spot-checks/${completing.id}/complete`, { ...form, notes: form.notes || null });
      toast.success(`Spot check ${completing.check_number} recorded`);
      setCompleting(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="spotchecks-panel">
      {loading ? (
        <div className="flex items-center gap-2 text-slate-600" data-testid="spotchecks-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-600" data-testid="spotchecks-empty">No spot checks recorded.</p>
      ) : (
        items.map((c) => (
          <div key={c.id} className="rounded-md border border-border p-3 space-y-2" data-testid={`spotcheck-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <CalendarCheck className="h-4 w-4 text-primary" />
              <span className="font-mono text-xs text-slate-500">{c.check_number}</span>
              <span className="text-sm font-medium text-slate-800">{c.staff_name}</span>
              {c.child_name && <span className="text-xs text-slate-500">· {c.child_name}</span>}
              <span className="text-xs text-slate-500">{c.scheduled_date}</span>
              <Badge className={STATUS_BADGES[c.status] || ""}>{c.status}</Badge>
              {c.status === "scheduled" && (
                <Button size="sm" data-testid={`spotcheck-complete-${c.id}`} className="ml-auto h-9 bg-primary hover:bg-brand-hover text-white"
                  onClick={() => { setCompleting(c); setForm(EMPTY); }}>
                  Record check
                </Button>
              )}
            </div>
            {c.status === "completed" && (
              <div className="text-xs text-slate-600 space-y-1" data-testid={`spotcheck-result-${c.id}`}>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {CHECKS.map(([k, label]) => (
                    <span key={k} className="inline-flex items-center gap-1">
                      {c[k] === "yes" ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <X className="h-3.5 w-3.5 text-red-500" />}
                      {label}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {RATINGS.map(([k, label]) => (
                    <span key={k}>{label}: <strong>{c[k]}/5</strong></span>
                  ))}
                </div>
                {c.notes && <p className="text-slate-500">Notes: {c.notes}</p>}
                <p className="text-slate-400">Completed by {c.completed_by_name} · {(c.completed_at || "").slice(0, 10)}</p>
              </div>
            )}
          </div>
        ))
      )}

      <Dialog open={!!completing} onOpenChange={(v) => !v && setCompleting(null)}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="spotcheck-complete-dialog">
          <DialogHeader>
            <DialogTitle>Record spot check {completing?.check_number}</DialogTitle>
            <DialogDescription>
              {completing?.staff_name}{completing?.child_name ? ` · ${completing.child_name}` : ""} · scheduled {completing?.scheduled_date}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {CHECKS.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <Label className="text-sm">{label}</Label>
                <YesNo value={form[key]} onChange={(v) => setForm((f) => ({ ...f, [key]: v }))} testid={`spotcheck-${key}`} />
              </div>
            ))}
            {RATINGS.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <Label className="text-sm">{label}</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" data-testid={`spotcheck-${key}-${n}`}
                      onClick={() => setForm((f) => ({ ...f, [key]: n }))}
                      className={`h-9 w-9 rounded-md border text-sm font-semibold transition-colors duration-150 ${
                        form[key] === n ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-border hover:border-primary/50"
                      }`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="spotcheck-notes">Notes</Label>
              <Textarea id="spotcheck-notes" data-testid="spotcheck-notes-input" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3}
                placeholder="Anything observed worth recording…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(null)}>Cancel</Button>
            <Button data-testid="spotcheck-complete-save" disabled={saving || !valid} onClick={save}
              className="bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save spot check"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
